import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Environment } from '../../core/config/environment';
import { useAutenticacaoContext } from '../../core/contexts/AutenticacaoContext';
import { IItemPedidoKDS } from '../interfaces/IKDS';

interface WebSocketKDSContextType {
    ultimoItemAtualizado?: IItemPedidoKDS;
    ultimoPedidoPronto?: string;
    registrarCallback: (callback: () => void) => void;
    removerCallback: (callback: () => void) => void;
    entrarSalaKDS: (sala: string) => void;
}

const WebSocketKDSContext = createContext<WebSocketKDSContextType>({
    registrarCallback: () => {},
    removerCallback: () => {},
    entrarSalaKDS: () => {},
});

export const useWebSocketKDSContext = () => useContext(WebSocketKDSContext);

export const WebSocketKDSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [ultimoItemAtualizado, setUltimoItemAtualizado] = useState<IItemPedidoKDS>();
    const [ultimoPedidoPronto, setUltimoPedidoPronto] = useState<string>();
    const { getToken, estabelecimento } = useAutenticacaoContext();
    const socketRef = useRef<Socket>();
    const callbacksRef = useRef<Set<() => void>>(new Set());

    useEffect(() => {
        const token = getToken("TOKEN_ESTABELECIMENTO_KEY");
        if (!token || !estabelecimento) return;

        const socket = io(Environment.MS_PEDIDOS_SOCKET, {
            auth: { token },
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
        });

        socketRef.current = socket;

        socket.on('kds:item-status', (item: IItemPedidoKDS) => {
            setUltimoItemAtualizado(item);
            callbacksRef.current.forEach(cb => { try { cb(); } catch (e) {} });
        });

        socket.on('kds:pedido-pronto', ({ pedidoUuid }: { pedidoUuid: string }) => {
            setUltimoPedidoPronto(pedidoUuid);
            callbacksRef.current.forEach(cb => { try { cb(); } catch (e) {} });
        });

        socket.on('kds:novo-pedido', () => {
            callbacksRef.current.forEach(cb => { try { cb(); } catch (e) {} });
        });

        socket.on('kds:processo-desbloqueado', () => {
            callbacksRef.current.forEach(cb => { try { cb(); } catch (e) {} });
        });

        socket.on('kds:processo-status', () => {
            callbacksRef.current.forEach(cb => { try { cb(); } catch (e) {} });
        });

        socket.on('kds:pedido-liberado', () => {
            callbacksRef.current.forEach(cb => { try { cb(); } catch (e) {} });
        });

        socket.on('kds:item-disparado', () => {
            callbacksRef.current.forEach(cb => { try { cb(); } catch (e) {} });
        });

        socket.on('kds:pedido-aguardando-disparo', () => {
            callbacksRef.current.forEach(cb => { try { cb(); } catch (e) {} });
        });

        return () => { socket.disconnect(); };
    }, [estabelecimento]);

    const registrarCallback = (callback: () => void) => callbacksRef.current.add(callback);
    const removerCallback = (callback: () => void) => callbacksRef.current.delete(callback);
    const entrarSalaKDS = (sala: string) => socketRef.current?.emit('kds:entrar-sala', { sala });

    return (
        <WebSocketKDSContext.Provider value={{ ultimoItemAtualizado, ultimoPedidoPronto, registrarCallback, removerCallback, entrarSalaKDS }}>
            {children}
        </WebSocketKDSContext.Provider>
    );
};
